import type { AlternativeSuggestion } from '../../core/model/availability';
import type { TimeSlot } from '../../core/model/restaurant';
import { Formatters } from '../../core/util/formatters';
import { today } from '../../core/util/datetime';
import { S } from '../../i18n/strings';
import type { AvailabilityState } from '../../state/availabilityState';
import { EmptyState, ErrorState, SkeletonBlock } from './StateContainer';

/**
 * Horarios reservables y, cuando no hay, las alternativas.
 *
 * "Sin mesas" nunca es un callejón sin salida: el servidor devuelve alternativas y aquí se
 * presentan como acciones, no como texto informativo.
 */
export function AvailabilityPanel({
  state,
  selectedToken,
  onSelectSlot,
  onSelectAlternative,
  onRetry,
}: {
  state: AvailabilityState;
  selectedToken?: string | null;
  onSelectSlot: (slot: TimeSlot) => void;
  onSelectAlternative?: (alternative: AlternativeSuggestion) => void;
  onRetry?: () => void;
}) {
  switch (state.status) {
    case 'LOADING':
      return <SkeletonBlock height={80} />;

    case 'ERROR':
      return <ErrorState error={state.error} onRetry={onRetry} />;

    case 'CLOSED_THAT_DAY':
      return (
        <EmptyState
          icon="🚪"
          title={S.restaurantClosedToday}
          body={S.availabilityNoneBody}
        />
      );

    case 'NO_AVAILABILITY':
      return (
        <div className="stack stack--tight">
          <EmptyState icon="🕑" title={S.availabilityNoneTitle} body={S.availabilityNoneBody} />
          {state.availability.alternatives.length > 0 && (
            <section className="section">
              <h3>{S.availabilityAlternatives}</h3>
              <div className="row">
                {state.availability.alternatives.map((alternative, index) => (
                  <button
                    key={`${alternative.kind}-${alternative.date}-${alternative.time}-${index}`}
                    type="button"
                    className="chip"
                    onClick={() => onSelectAlternative?.(alternative)}
                  >
                    {describeAlternative(alternative)}
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      );

    case 'AVAILABLE':
      return (
        <div className="slot-row">
          {state.availability.slots.map((slot) => (
            <button
              key={slot.token}
              type="button"
              className={`slot ${selectedToken === slot.token ? 'slot--selected' : ''}`}
              disabled={!slot.available}
              aria-pressed={selectedToken === slot.token}
              onClick={() => onSelectSlot(slot)}
            >
              {Formatters.time(slot.time)}
              {slot.label && <span className="muted"> · {slot.label}</span>}
            </button>
          ))}
        </div>
      );
  }
}

function describeAlternative(alternative: AlternativeSuggestion): string {
  const time = Formatters.time(alternative.time);
  switch (alternative.kind) {
    case 'NEARBY_TIME':
      return `${S.alternativeNearbyTime}: ${time}`;
    case 'ANOTHER_DAY':
      return `${Formatters.friendlyDate(alternative.date, today())} · ${time}`;
    case 'SMALLER_PARTY':
      return `${Formatters.partySize(alternative.partySize)} · ${time}`;
  }
}
